import useAssetRetrieval from "./useAssetRetrieval";
import { useMemo } from "react";
import { BoxType } from "types/assetRetrieval";
import { TotalRowsPerBox } from "./useAssetRetrievalBulkValidation";

interface PricingPerBoxEntry {
  isPricingLoading: boolean;
  basePricePerRow: number;
  basePrice: number;
  insuranceAmount: number;
  discountPercentage: number;
  discountAmount: number;
  taxPercentage: number;
  taxAmount: number;
  processingFeePerRow: number;
  processingFee: number;
  serviceFeeInCents: number;
  total: number;
}

const buildPricingEntry = (
  pricing: ReturnType<typeof useAssetRetrieval>["pricing"],
  isPricingLoading: boolean,
  totalRows: number
): PricingPerBoxEntry => {
  const total = pricing?.totalInCents ? Number(pricing.totalInCents / 100) : 0;
  const basePrice = pricing?.basePriceInCents ? Number(pricing.basePriceInCents / 100) : 0;
  const basePricePerRow = basePrice && totalRows ? basePrice / totalRows : 0;
  const insuranceAmount = pricing?.insuranceTotalInCents ? Number(pricing.insuranceTotalInCents / 100) : 0;
  const discountPercentage = pricing?.discountPercentage || 0;
  const discountAmount = pricing?.discountInCents ? Number(pricing.discountInCents / 100) : 0;
  const taxPercentage = pricing?.taxPercentage || 0;
  const taxAmount = pricing?.taxInCents ? Number(pricing.taxInCents / 100) : 0;
  const processingFee = pricing?.miscellaneousFeesInCents ? Number(pricing.miscellaneousFeesInCents / 100) : 0;
  const processingFeePerRow = pricing?.miscellaneousFeesPerItemInCents
    ? Number(pricing.miscellaneousFeesPerItemInCents / 100)
    : 0;
  const serviceFeeInCents = pricing?.serviceFeeInCents ? Number(pricing.serviceFeeInCents / 100) : 0;
  return {
    isPricingLoading,
    basePricePerRow,
    basePrice,
    insuranceAmount,
    processingFeePerRow,
    processingFee,
    taxPercentage,
    taxAmount,
    discountPercentage,
    discountAmount,
    serviceFeeInCents,
    total,
  };
};

export default function useAssetRetrievalPricingPerBox(
  totalRowsPerBox: TotalRowsPerBox,
  virtualPickup: boolean,
  country?: string
) {
  const laptopResult = useAssetRetrieval({
    quantity: totalRowsPerBox?.laptop?.quantity ?? 0,
    insuranceTotalInCents: totalRowsPerBox?.laptop?.insuranceInCents ?? 0,
    virtualPickup,
    boxType: "laptop" as BoxType,
    country,
  });
  const mobilePhoneResult = useAssetRetrieval({
    quantity: totalRowsPerBox?.mobile_phone?.quantity ?? 0,
    insuranceTotalInCents: totalRowsPerBox?.mobile_phone?.insuranceInCents ?? 0,
    virtualPickup,
    boxType: "mobile_phone" as BoxType,
    country,
  });
  const desktopResult = useAssetRetrieval({
    quantity: totalRowsPerBox?.desktop?.quantity ?? 0,
    insuranceTotalInCents: totalRowsPerBox?.desktop?.insuranceInCents ?? 0,
    virtualPickup,
    boxType: "desktop" as BoxType,
    country,
  });
  const monitorResult = useAssetRetrieval({
    quantity: totalRowsPerBox?.monitor?.quantity ?? 0,
    insuranceTotalInCents: totalRowsPerBox?.monitor?.insuranceInCents ?? 0,
    virtualPickup,
    boxType: "monitor" as BoxType,
    country,
  });

  const pricingPerBox = useMemo<Record<string, PricingPerBoxEntry>>(
    () => ({
      laptop: buildPricingEntry(laptopResult.pricing, laptopResult.loading, totalRowsPerBox?.laptop?.quantity ?? 0),
      mobile_phone: buildPricingEntry(
        mobilePhoneResult.pricing,
        mobilePhoneResult.loading,
        totalRowsPerBox?.mobile_phone?.quantity ?? 0
      ),
      desktop: buildPricingEntry(desktopResult.pricing, desktopResult.loading, totalRowsPerBox?.desktop?.quantity ?? 0),
      monitor: buildPricingEntry(monitorResult.pricing, monitorResult.loading, totalRowsPerBox?.monitor?.quantity ?? 0),
    }),
    [
      laptopResult.pricing,
      laptopResult.loading,
      mobilePhoneResult.pricing,
      mobilePhoneResult.loading,
      desktopResult.pricing,
      desktopResult.loading,
      monitorResult.pricing,
      monitorResult.loading,
      totalRowsPerBox,
    ]
  );

  return pricingPerBox;
}
